import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Chip,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Skeleton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  People,
  PersonAdd,
  TrendingUp,
  CalendarMonth,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import StaffAppBar from './StaffAppBar';
import { performLogout } from '../services/sessionExpiry';
import {
  getLogins7Days,
  getLogins30Days,
  getTotalAccounts,
  getNewAccounts7Days,
  getAccounts,
  formatDate,
  formatNumber
} from '../services/adminService';

/**
 * Statistics Card Component
 */
const StatCard = ({ title, value, icon, loading, color = 'primary' }) => {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Box 
            sx={{ 
              p: 1, 
              borderRadius: 1, 
              bgcolor: `${color}.light`, 
              color: `${color}.contrastText`,
              mr: 2 
            }}
          >
            {icon}
          </Box>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            {title}
          </Typography>
        </Box>
        {loading ? (
          <Skeleton variant="text" height={40} />
        ) : (
          <Typography variant="h4" component="div" color="text.primary" sx={{ fontWeight: 600 }}>
            {formatNumber(value)}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

/**
 * User Accounts Table Component
 */
const UserAccountsTable = ({ accounts, pagination, loading, onPageChange, onSortChange, sortBy, sortOrder, onRowClick }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleChangePage = (event, newPage) => {
    onPageChange(newPage + 1); // API uses 1-based pagination
  };

  const handleSort = (field) => {
    const newOrder = sortBy === field && sortOrder === 'asc' ? 'desc' : 'asc';
    onSortChange(field, newOrder);
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            User Accounts
          </Typography>
          {[...Array(10)].map((_, index) => (
            <Skeleton key={index} variant="rectangular" height={60} sx={{ mb: 1 }} />
          ))}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            User Accounts
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {pagination?.total_items ? `${formatNumber(pagination.total_items)} total accounts` : ''}
          </Typography>
        </Box>
        
        <TableContainer component={Paper} variant="outlined">
          <Table size={isMobile ? "small" : "medium"}>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={sortBy === 'first_name'}
                    direction={sortBy === 'first_name' ? sortOrder : 'asc'}
                    onClick={() => handleSort('first_name')}
                  >
                    First Name
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortBy === 'last_name'}
                    direction={sortBy === 'last_name' ? sortOrder : 'asc'}
                    onClick={() => handleSort('last_name')}
                  >
                    Last Name
                  </TableSortLabel>
                </TableCell>
                {!isMobile && (
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === 'username'}
                      direction={sortBy === 'username' ? sortOrder : 'asc'}
                      onClick={() => handleSort('username')}
                    >
                      Username
                    </TableSortLabel>
                  </TableCell>
                )}
                <TableCell>
                  <TableSortLabel
                    active={sortBy === 'last_login'}
                    direction={sortBy === 'last_login' ? sortOrder : 'asc'}
                    onClick={() => handleSort('last_login')}
                  >
                    Last Login
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortBy === 'created_at'}
                    direction={sortBy === 'created_at' ? sortOrder : 'asc'}
                    onClick={() => handleSort('created_at')}
                  >
                    Created
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortBy === 'login_count'}
                    direction={sortBy === 'login_count' ? sortOrder : 'asc'}
                    onClick={() => handleSort('login_count')}
                  >
                    Logins
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {accounts?.map((account) => (
                <TableRow
                  key={account.id}
                  hover={Boolean(onRowClick)}
                  onClick={onRowClick ? () => onRowClick(account.id) : undefined}
                  sx={{ cursor: onRowClick ? 'pointer' : 'default' }}
                >
                  <TableCell>{account.first_name || 'N/A'}</TableCell>
                  <TableCell>{account.last_name || 'N/A'}</TableCell>
                  {!isMobile && (
                    <TableCell sx={{ maxWidth: isMobile ? 100 : 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {account.username}
                    </TableCell>
                  )}
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(account.last_login)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(account.created_at)}
                    </Typography>
                    {isMobile && (
                      <Typography variant="caption" color="text.secondary">
                        {account.username}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={formatNumber(account.login_count)} 
                      size="small" 
                      color={account.login_count > 10 ? 'success' : 'default'}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        
        {pagination && (
          <TablePagination
            component="div"
            count={pagination.total_items}
            page={pagination.current_page - 1} // Component uses 0-based pagination
            onPageChange={handleChangePage}
            rowsPerPage={pagination.page_size}
            rowsPerPageOptions={[]} // Disable rows per page selector
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
            }
          />
        )}
      </CardContent>
    </Card>
  );
};

/**
 * Main Admin Dashboard Component
 */
function AdminDashboard() {
  const navigate = useNavigate();

  // Dashboard statistics state
  const [stats, setStats] = useState({
    logins_7_days: 0,
    logins_30_days: 0,
    total_accounts: 0,
    new_accounts_7_days: 0,
  });
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState(null);

  // Accounts table state
  const [accounts, setAccounts] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [accountsLoading, setAccountsLoading] = useState(true);
  const [accountsError, setAccountsError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');

  // Rows only open the detail page for operators who can actually view it —
  // otherwise the click lands on a 404, which reads as a broken dashboard.
  const permissions = JSON.parse(localStorage.getItem('userPermissions') || '[]');
  const mayViewUserDetails = permissions.includes('can_view_user_details');

  /**
   * Fetch current user data and verify can_view_admin_dash permission
   */
  const fetchUserData = useCallback(async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch('/api/user', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();

        // Verify can_view_admin_dash permission
        if (!data.permissions || !data.permissions.includes('can_view_admin_dash')) {
          navigate('/dashboard');
        }
      } else if (response.status === 401) {
        performLogout(navigate);
      }
    } catch (error) {
      console.error('Failed to fetch user data:', error);
    }
  }, [navigate]);

  /**
   * Fetch dashboard statistics
   */
  const fetchStats = useCallback(async () => {
    try {
      setStatsLoading(true);
      setStatsError(null);
      
      // Fetch all statistics in parallel for better performance
      const [
        logins7DaysData,
        logins30DaysData,
        totalAccountsData,
        newAccounts7DaysData
      ] = await Promise.all([
        getLogins7Days(),
        getLogins30Days(),
        getTotalAccounts(),
        getNewAccounts7Days()
      ]);
      
      // Combine the results into the expected format
      const combinedStats = {
        logins_7_days: logins7DaysData.count,
        logins_30_days: logins30DaysData.count,
        total_accounts: totalAccountsData.count,
        new_accounts_7_days: newAccounts7DaysData.count,
      };
      
      setStats(combinedStats);
    } catch (error) {
      console.error('Failed to fetch statistics:', error);
      setStatsError(error.message);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  /**
   * Fetch user accounts with pagination
   */
  const fetchAccounts = useCallback(async () => {
    try {
      setAccountsLoading(true);
      setAccountsError(null);
      
      const data = await getAccounts({
        page: currentPage,
        page_size: 25,
        sort_by: sortBy,
        sort_order: sortOrder,
      });
      
      setAccounts(data.accounts);
      setPagination(data.pagination);
    } catch (error) {
      console.error('Failed to fetch accounts:', error);
      setAccountsError(error.message);
    } finally {
      setAccountsLoading(false);
    }
  }, [currentPage, sortBy, sortOrder]);

  /**
   * Handle page change
   */
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  /**
   * Handle sort change
   */
  const handleSortChange = (field, order) => {
    setSortBy(field);
    setSortOrder(order);
    setCurrentPage(1); // Reset to first page when sorting changes
  };

  // Verify the viewer's permission and load statistics once, on mount only.
  // fetchUserData/fetchStats are intentionally omitted from the deps array:
  // this effect must run exactly once regardless of their identity, and
  // account fetching is owned entirely by the effect below.
  useEffect(() => {
    fetchUserData();
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch accounts on mount and again whenever pagination/sorting changes.
  // fetchAccounts is a useCallback keyed on [currentPage, sortBy, sortOrder],
  // so its identity changes exactly when a refetch is needed - including the
  // initial mount, which is why no separate "initial" fetch is needed here.
  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);


  return (
    <Box sx={{ flexGrow: 1 }}>
      <StaffAppBar />

      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
          Admin Dashboard
        </Typography>

        {/* Error Messages */}
        {statsError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            Failed to load statistics: {statsError}
          </Alert>
        )}

        {/* Statistics Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <StatCard
              title="7-Day Logins"
              value={stats.logins_7_days}
              icon={<TrendingUp />}
              loading={statsLoading}
              color="success"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <StatCard
              title="30-Day Logins"
              value={stats.logins_30_days}
              icon={<CalendarMonth />}
              loading={statsLoading}
              color="info"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <StatCard
              title="Total Accounts"
              value={stats.total_accounts}
              icon={<People />}
              loading={statsLoading}
              color="primary"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <StatCard
              title="New This Week"
              value={stats.new_accounts_7_days}
              icon={<PersonAdd />}
              loading={statsLoading}
              color="warning"
            />
          </Grid>
        </Grid>

        {/* Error Message for Accounts */}
        {accountsError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            Failed to load user accounts: {accountsError}
          </Alert>
        )}

        {/* User Accounts Table */}
        <UserAccountsTable
          accounts={accounts}
          pagination={pagination}
          loading={accountsLoading}
          onPageChange={handlePageChange}
          onSortChange={handleSortChange}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onRowClick={mayViewUserDetails ? (id) => navigate(`/admin/user/${id}`) : undefined}
        />
      </Container>
    </Box>
  );
}

export default AdminDashboard;